import React from 'react';
import { Grid, Tooltip } from '@mui/material';

import { GridListItem, type ItemLayoutProps } from 'app/main/components/GridList';
import { ButtonLink, Icon, ImageAvatar, SquareChip, TagsArray, Text, useSettings, Utils } from 'app/main/components';
import { DateRangeSquareChip, DurationSquareChip } from 'app/main/components/list-items';
import buttons from './ActivitiesButtons';

export default function ActivitiesListItem(props: ItemLayoutProps) {
	const { sysSettings, t } = useSettings();

	const isNotClosed = [1, 2, 5].includes(props.model.acti_acst_id);
	const daysToExpire = Utils.parseDate(props.model.acti_d_to)?.diff(Utils.today(), 'days');

	const avatar = (
		<ImageAvatar
			dimensions={44}
			module="users"
			uid={props.model.acti_user_assigned_uid}
			avatar={props.model.user_assigned_avatar}
			initials={props.model.acti_title_initials}
			labelBadge={props.model.acst_title_initials}
			labelBadgeTooltip={props.model.acst_title}
			badgeColor={props.model.acst_color}
		/>
	);

	return (
		<GridListItem avatar={avatar} buttons={buttons.view?.itemButtons}>
			<Grid container justifyContent="space-between" spacing={1}>
				<Grid size={props.width >= 600 ? 6 : 12}>
					<Grid container>
						<Grid size={12} className="inline-flex items-center">
							{/* WARNING */}
							{props.model.acti_warning_desc && (
								<Tooltip title={props.model.acti_warning_desc} arrow>
									<Icon className="mr-2" icon="alert-circle-outline" color="warning.main" />
								</Tooltip>
							)}
							{/* BLOCKED */}
							{props.model.acti_blocked_desc && (
								<Tooltip title={props.model.acti_blocked_desc} arrow>
									<Icon className="mr-2" icon="block" color="error.main" />
								</Tooltip>
							)}
							{/* TITLE */}
							<Text
								className="font-600"
								variant="subtitle2"
								to={{ module: 'activities', uid: props.model.acti_uid }}
							>
								{props.model.comp_title && `${props.model.comp_title} - `}
								{props.model.acti_title}
							</Text>
						</Grid>
						{/* TYPE */}
						<Grid>
							{props.model.acty_title && (
								<SquareChip label={props.model.acty_title} backgroundColor={props.model.acty_color} />
							)}
						</Grid>
						{/* DATE FROM - TO */}
						<Grid>
							<DateRangeSquareChip
								from={props.model.acti_d_from}
								to={props.model.acti_d_to}
								icon={<Icon icon="clock-outline" size={16} />}
								backgroundColor={theme => {
									if (isNotClosed && daysToExpire !== undefined) {
										if (daysToExpire <= (sysSettings.activities_expiring_tier1 || 0))
											return theme.palette.error.main;
										if (daysToExpire <= sysSettings.activities_expiring_tier2)
											return theme.palette.warning.main;
									}
								}}
							/>
						</Grid>
						{/* EXPECTED DURATION */}
						{(props.model.acti_length_hours > 0 || props.model.acti_length_minutes > 0) && (
							<Grid>
								<DurationSquareChip
									id="activitiesListItem.expectedDuration"
									defaultMessage={t('DURATION_EXPECTED')}
									duration={{
										hours: props.model.acti_length_hours,
										minutes: props.model.acti_length_minutes
									}}
								/>
							</Grid>
						)}
						{/* TAGS */}
						<Grid>
							<TagsArray tags={props.model.acti_tags} changeFilter={props.changeFilter} />
						</Grid>
					</Grid>
				</Grid>
				{props.width >= 600 && (
					<Grid size={4}>
						<Grid container>
							{/* INTERNAL ASSIGNED USER */}
							<Grid size={12}>
								<ButtonLink
									href={`/users/details/${props.model.acti_user_assigned_uid}`}
									icon="account-circle"
									fallback={t('VIEW_ITEM_NO_INTUSER')}
								>
									{props.model.user_assigned_title}
								</ButtonLink>
							</Grid>
							{/* EXTERNAL ASSIGNED USER */}
							<Grid size={12}>
								<ButtonLink
									href={`/users/details/${props.model.acti_user_assigned_2_uid}`}
									icon="account-circle"
									fallback={t('VIEW_ITEM_NO_EXTUSER')}
								>
									{props.model.user_assigned_2_title}
								</ButtonLink>
							</Grid>
						</Grid>
					</Grid>
				)}
			</Grid>
		</GridListItem>
	);
}
